<template>
	<div v-if="! hideButtons" :class="['input-number-group', buttonSize]">
		<icon-button
			v-if="! hideButtons"
			variant="subtract"
			:size="size"
			@click="addAmount(-stepSize)"
		/>
		<input
			ref="input"
			:disabled="disabled"
			:class="['input-number', buttonSize, {'hide-spinner': ! showSpinner}]"
			@change.capture="change"
			@keydown.capture="keyDown"
			v-model="stringValue"
			:type="inputType"
			pattern="[0-9]*"
			inputmode="numeric"
			novalidate
		/>
		<icon-button
			v-if="! hideButtons"
			variant="add"
			:size="size"
			@click="addAmount(stepSize)"
		/>
	</div>
	<input
		v-else
		ref="input"
		:disabled="disabled"
		:class="['input-number', {'hide-spinner': ! showSpinner}]"
		@change.capture="change"
		@keydown.capture="keyDown"
		v-model="stringValue"
		:type="inputType"
		pattern="[0-9]*"
		inputmode="numeric"
		novalidate
	/>
</template>

<script>
function doGetCaretPosition (ctrl) {
	let caretPos = 0;

	if (ctrl.selectionStart || ctrl.selectionStart === 0) { // Standard.
		caretPos = ctrl.selectionStart;
	} else if (document.selection) { // Legacy IE
		ctrl.focus();
		const sel = document.selection.createRange();
		sel.moveStart('character', -ctrl.value.length);
		caretPos = sel.text.length;
	}

	return (caretPos);
}

function setCaretPosition (ctrl, pos) {
	if(ctrl.type !== 'number') {
		if (ctrl.setSelectionRange) {
			ctrl.focus();
			ctrl.setSelectionRange(pos, pos);
		} else if (ctrl.createTextRange) {
			const range = ctrl.createTextRange();
			range.collapse(true);
			range.moveEnd('character', pos);
			range.moveStart('character', pos);
			range.select();
		}
	}
}

export default {
	data () {
		return {
			stringValue: this.value.toFixed(this.precision),
			internalValue: this.value,
		};
	},
	props: {
		min: {
			type: Number,
		},
		max: {
			type: Number,
		},
		value: {
			type: Number,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		hideButtons: {
			type: Boolean,
			default: false,
		},
		stepSize: {
			type: Number,
			default: 1,
		},
		/* Button sizes:
			large
			normal
			small
			tiny
		*/
		size: {
			type: String,
			default: 'normal',
		},
		noFontResize: {
			type: Boolean,
			default: false,
		},
		inputType: {
			type: String,
			default: 'number',
		},
		showSpinner: {
			type: Boolean,
			default: false,
		},
		paginator: {
			type: Boolean,
			default: false,
		},
		transparentIconButtons: {
			type: Boolean,
			default: false,
		},
	},
	mounted () {
		if (this.internalValue < this.min) {
			this.internalValue = this.min;
		} else	if (this.internalValue > this.max) {
			this.internalValue = this.max;
		}
	},
	methods: {
		keyDown (ev) {
			if (ev.key !== 'ArrowUp' && ev.key !== 'ArrowDown') return;
			ev.preventDefault();
			ev.stopPropagation();
			const direction = ev.key === 'ArrowUp' ? 1 : -1;
			let amount;
			if (! isNaN(this.stringValue)) amount = Math.round(this.stringValue / this.stepSize);
			else amount = Math.round(this.internalValue / this.stepSize);
			const modifier = (ev.shiftKey ? 5 : 1) * (ev.ctrlKey ? 10 : 1);
			amount = Math[direction > 0 ? 'floor' : 'ceil'](amount / modifier);
			amount += direction;
			amount *= modifier;
			this.addAmount((amount * this.stepSize - this.internalValue));
		},
		addAmount (amount) {
			this.internalValue += amount;
			this.$nextTick(() => {
				this.$emit('change', this.internalValue);
			});
		},
		change () {
			this.internalValue = this.stringValue;
			this.$nextTick(() => {
				this.$emit('change', this.internalValue);
			});
		},
		round (value) {
			return Math.round(value / this.stepSize) * this.stepSize;
		},
	},
	computed: {
		minusStyle () {
			return {
				deactivated: this.min !== undefined && this.value <= this.min,
			};
		},
		plusStyle () {
			const deactivated = this.max !== undefined && this.value >= this.max;
			return deactivated ? {
				color: 'grey',
			} : {

			};
		},
		isFraction () {
			return this.stepSize % 1 !== 0;
		},
		precision () {
			const maxPrecision = 5;
			const numstring = ((this.stepSize * Math.pow(10, maxPrecision)).toFixed(0));
			return Math.max(0, maxPrecision - numstring.length + numstring.search(/0*$/));
		},
		validInput () {
			const neg = this.min < 0 ? '-?' : '';
			const frac = this.isFraction ? '(\\d*\\.)?' : '';
			return new RegExp('^' + neg + frac + '\\d*$');
		},
		buttonSize () {
			// Checks size and adds class accordingly unless the size is normal since it's the default
			if (this.size !== 'normal') {
				return '_' + this.size;
			} else {
				return null;
			}
		},
	},
	watch: {
		min (min) {
			if (this.internalValue < min) {
				this.internalValue = min;
			}
		},
		max (max) {
			if (this.internalValue > max) {
				this.internalValue = max;
			}
		},
		value (to, frm) {
			this.internalValue = to;
		},
		internalValue (to, frm) {
			if (isNaN(to)) {
				to = frm;
				this.internalValue = to;
			} else {
				to = this.round(to * 1);
				if (to < this.min) {
					this.$emit('mincapped', {
						capValue: this.min,
						triedValue: to,
					});
					to = this.min;
				}
				if (to > this.max) {
					this.$emit('maxcapped', {
						capValue: this.max,
						triedValue: to,
					});
					to = this.max;
				}
				this.internalValue = to;
				this.$emit('input', to);
				if (to !== this.stringValue * 1) {
					const newString = to.toFixed(this.precision);
					const cursorPosition = doGetCaretPosition(this.$refs.input);
					this.stringValue = newString;
					if (this.$refs.input === document.activeElement) {
						this.$nextTick(() => setCaretPosition(this.$refs.input, cursorPosition));
					}
				}
			}
		},
		stringValue (to, frm) {
			if (to.slice(-1) === '%' && this.validInput.test(to.slice(0, -1))) {
				this.stringValue = (this.min + Math.round((to.slice(0, -1) * 0.01) * (this.max - this.min))) + '';
				this.internalValue = this.stringValue * 1;
				return;
			}
			if (! this.validInput.test(to)) {
				const cursorPosition = doGgisteretCaretPosition(this.$refs.input) - 1;
				this.stringValue = frm;
				if (this.$refs.input === document.activeElement) {
					this.$nextTick(() => setCaretPosition(this.$refs.input, cursorPosition));
				}
				return;
			} else {
				let numVal = to * 1;
				numVal = numVal * 1;

				if (this.round(numVal) !== numVal) return;
				if (numVal < this.min) return;
				if (numVal > this.max) return;

				this.internalValue = numVal;
			}
		},
	},
};
</script>
